import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  IconButton,
  Tabs,
  Tab,
  Autocomplete,
  Tooltip
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import DirectionsBusRoundedIcon from '@mui/icons-material/DirectionsBusRounded';
import AltRouteRoundedIcon from '@mui/icons-material/AltRouteRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import { useThemeMode } from '../context/ThemeContext';
import TripsGridAlerts from '../components/TripsGridAlerts';
import PenalizacaoPanel from '../components/penalizacao/PenalizacaoPanel';
import MapaTrafego from './Mapa_Trafego';

// ÍCONES PARA STATUS NA TABELA (com sufixo 'Status')
import IconExecutadaStatus from '../assets/check.png';
import IconNaoRealizadaStatus from '../assets/x.png';
import IconNaoIniciadaStatus from '../assets/clock.png';
import IconEmAndamento from '../assets/onibus.png';
import IconAtrasadoStatus from '../assets/atrasado.png';
import IconCanceladoStatus from '../assets/cancelado.png';
import IconExtraStatus from '../assets/extra.png';
import IconAdiantamento from '../assets/adiantamento.png';

// ÍCONES PARA OS CARDS (sem conflito de nomes)
import IconPlanejada from '../assets/planejada.png';
import IconExecutada from '../assets/executada.png';
import IconNaoRealizada from '../assets/naoRealizada.png';
import IconNaoIniciada from '../assets/naoIniciada.png';
import IconPassageiros from '../assets/passageiros.png';
import IconAtrasado from '../assets/atrasado.png';
import IconCancelado from '../assets/cancelado.png';
  import IconExtra from '../assets/extra.png';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://control-api-connectbus.viacaosaopedro.com';
console.log('🔍 TrafegoPage - API_BASE (env):', process.env.REACT_APP_API_BASE);
console.log('🔍 TrafegoPage - API_BASE (final):', API_BASE);

// 📦 Cache em memória para dados de ocioso fechado (2 minutos)
const ociosoFechadoCache = {
  dados: null,
  timestamp: null,
  CACHE_TIME: 2 * 60 * 1000 // 2 minutos (sincronizado com backend)
};


function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}


  // Abas em estilo "pill"
  const PillTabs = styled(Tabs)(({ theme }) => ({
    minHeight: 0,
    height: 44,
    padding: 4,
    borderRadius: 999,
    '& .MuiTabs-indicator': { display: 'none' }, // sem linha, estilo pill
  }));

  const PillTab = styled(Tab)(({ theme }) => ({
    minHeight: 0,
    height: 36,
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: 999,
    padding: '6px 14px',
    margin: '0 3px',
    letterSpacing: 0.2,
    color: theme.palette.text.secondary,
    '& .MuiTab-iconWrapper': { marginRight: 8 },
    '&:hover': {
      backgroundColor: alpha(theme.palette.text.primary, 0.06),
    },
    '&.Mui-selected': {
      color: theme.palette.getContrastText(theme.palette.primary.main),
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.92)} 0%, ${theme.palette.primary.main} 100%)`,
      boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
    },
  }));

// Tooltip rico para os cards de Divergências (Ocioso/Quebra) — com faixa lateral por variante
function CardDivergenciasTooltip({ items, variant = 'ocioso', max = 12 }) {
  const list = Array.isArray(items) ? items : [];
  
  return (
    <Paper
      elevation={6}
      sx={(theme) => ({
        p: 1.5,
        borderRadius: 0.75,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        minWidth: 220,
        maxWidth: 390,
        maxHeight: 260,
        overflowY: 'auto',
        // faixa lateral colorida
        borderLeft: '4px solid',
        borderLeftColor:
          variant === 'quebra'
            ? theme.palette.error.main
            : '#EB9000', // default ocioso
      })}
    >
      {/* TÍTULO */}
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 900,
          letterSpacing: 0.4,
          mb: 1.25,
          textTransform: 'uppercase',
          color: variant === 'quebra' ? 'error.main' : 'warning.main'
        }}
      >
        {variant === 'quebra' ? '🚨 QUEBRAS DETECTADAS' : '📘 OCORRÊNCIAS DO DIA'}
      </Typography>

      {/* ITENS */}
      {list.length ? (
        list.slice(0, max).map((it, i) => (
          <Box key={i} sx={{ mb: i < list.length - 1 ? 1.5 : 0 }}>
            {variant === 'quebra' ? (
              // Formato específico para quebras: Veículo XXXX • Xmin parado (data inicio - data fim)
              <>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  <Box component="span" sx={{ fontWeight: 900 }}>
                    Veículo {String(it.veiculo || '-')}
                  </Box>
                </Typography>
                
                {it.quebras && it.quebras.length > 0 && (
                  <Box sx={{ ml: 1 }}>
                    {it.quebras.map((quebra, qIdx) => (
                      <Typography key={qIdx} variant="body2" sx={{ 
                        fontWeight: 500, 
                        mb: qIdx < it.quebras.length - 1 ? 0.3 : 0,
                        fontSize: '0.85rem'
                      }}>
                        • {quebra.duracao} parado ({quebra.horarioInicio} - {quebra.horarioFim})
                      </Typography>
                    ))}
                  </Box>
                )}
              </>
            ) : (
              // Formato original para outros tipos (ocioso)
              <>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Veículo:{' '}
                  <Box component="span" sx={{ fontWeight: 900 }}>
                    {String(it.veiculo || '-')}
                  </Box>
                </Typography>

                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Status:{' '}
                  <Box component="span" sx={{ fontWeight: 900 }}>
                    {String(it.status || 'OCIOSO FECHADO')}
                  </Box>
                </Typography>

                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Data:{' '}
                  <Box component="span" sx={{ fontWeight: 900 }}>
                    {String(it.data || '-')}
                  </Box>
                </Typography>

                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Hora:{' '}
                  <Box component="span" sx={{ fontWeight: 900 }}>
                    {String(it.hora || '-')}
                  </Box>
                </Typography>
              </>

            )}
          </Box>
        ))
      ) : (
        <Typography variant="body2">
          {variant === 'quebra' ? 'Nenhuma quebra detectada' : 'Sem ocorrências'}
        </Typography>
      )}

      {Array.isArray(items) && items.length > max && (
        <Typography variant="caption" sx={{ opacity: 0.8, mt: 1, display: 'block' }}>
          +{items.length - max} mais…
        </Typography>
      )}
    </Paper>
  );
}

export default function TrafegoPage() {
  const [linha, setLinha] = useState('');
  const [trajeto, setTrajeto] = useState('');
  // const [tab, setTab] = useState(0); // padrão antigo: iniciar em "Operação"
  const [tab, setTab] = useState(0);   // iniciar em "Operação"

  dayjs.locale('pt-br');

  // Hoje (local) às 03:00 → Amanhã 02:59
  const horaInicioPadrao = '03:00';
  const horaFimPadrao = '02:59';

  // ⬇️ ESTADOS DE DATA/HORA DEVEM VIR ANTES DO useMemo
  const [dataInicio, setDataInicio] = useState(dayjs());            // hoje
  const [horaInicio, setHoraInicio] = useState(horaInicioPadrao);
  const [dataFim, setDataFim] = useState(dayjs().add(1, 'day'));    // amanhã
  const [horaFim, setHoraFim] = useState(horaFimPadrao);

  // ⬇️ SÓ AGORA montamos o período a partir dos estados acima
  const periodoSelecionado = useMemo(() => {
    const ini = dayjs(`${dataInicio.format('YYYY-MM-DD')} ${horaInicio}:00`);
    const fim = dayjs(`${dataFim.format('YYYY-MM-DD')} ${horaFim}:00`);
    return { inicio: ini, fim: fim };
  }, [dataInicio, horaInicio, dataFim, horaFim]);

  // ... resto do componente


  const [viagens, setViagens] = useState([]);
  const [countdown, setCountdown] = useState(60);

  // adiciona um “marcador” que incrementa a cada refresh de dados
  const [refreshSeq, setRefreshSeq] = useState(0);

  const [indicadoresData, setIndicadoresData] = useState({
    planejadas: 0,
    executadas: 0,
    emAtraso: 0,
    naoRealizadas: 0,
    naoIniciadas: 0,
    canceladas: 0,
    extras: 0,
    penalizacoes: 0,
    passageiros: 0,
    emAndamento: 0,
    adiantadas: 0,
    ociosoFechado: 0,
    possivelQuebra: 0,
  });

  // Divergências (script-divergencias-fixed.mjs)
  const [divergenciasTooltip, setDivergenciasTooltip] = useState({ ocioso: [], quebra: [] });

  const [linhasDisponiveis, setLinhasDisponiveis] = useState([]);
  const [trajetosDisponiveis, setTrajetosDisponiveis] = useState([]);

  const [trajetosOriginais, setTrajetosOriginais] = useState([]);
  // Filtro ativo pelos cards: 'PLANEJADAS' | 'EXECUTADA' | 'NAO_REALIZADA' | 'NAO_INICIADA' | null
  const [statusFiltro, setStatusFiltro] = useState(null);

  const navigate = useNavigate();
  const { mode, setMode } = useThemeMode();

  const getStatusComIcone = (status) => {
    let icon;
    let label = status;

    switch (status) {
      case 'Executada':       icon = IconExecutadaStatus; break;
      case 'Não Realizada':   icon = IconNaoRealizadaStatus; break;
      case 'Não Iniciada':    icon = IconNaoIniciadaStatus; break;
      case 'Em Atraso':       icon = IconAtrasadoStatus; break;
      case 'Em Andamento':    icon = IconEmAndamento; break;
      case 'Cancelada':       icon = IconCanceladoStatus; break;
      case 'Extra':           icon = IconExtraStatus; break;
      case 'Adiantada':       icon = IconAdiantamento; break;   // ⬅️ NOVO
      default:                icon = null;
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        {icon && <img src={icon} alt={status} style={{ width: 22, height: 22, objectFit: 'contain' }} />}
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{label}</Typography>
      </Box>
    );
  };

  const isDark = mode === 'dark'; // useThemeMode

  const fmt2 = (n) =>
    new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      .format(Number(n || 0));

  const indicadores = [
    {
      label: 'Viagens Programadas',
      valor: indicadoresData.planejadas,
      icon: IconPlanejada,
      bg: isDark ? '#1e88e5' : '#e3f2fd',
      filterKey: 'PLANEJADAS',
    },
    {
      label: 'Viagens Realizadas',
      valor: indicadoresData.executadas,
      icon: IconExecutada,
      bg: isDark ? '#43a047' : '#e8f5e9',
      filterKey: 'EXECUTADA',
    },
    {
      label: 'Viagens em Andamento',
      valor: indicadoresData.emAndamento,
      icon: IconEmAndamento,
      bg: isDark ? '#4cafef' : '#e1f5fe',  // azul claro
      filterKey: 'EM_ANDAMENTO',
    },
    {
      label: 'Viagens Adiantadas',
      valor: indicadoresData.adiantadas,
      icon: IconAdiantamento,
      bg: isDark ? '#4CA6A8' : '#e0f7fa',   // azul clarinho
      filterKey: 'ADIANTADA',
    },    
    {
      label: 'Viagens em Atraso',
      valor: indicadoresData.emAtraso,
      icon: IconAtrasado,
      bg: isDark ? '#4E3A6D' : '#fff8e1',
      filterKey: 'EM_ATRASO',
    },
    {
      label: 'Viagens Não Realizadas',
      valor: indicadoresData.naoRealizadas,
      icon: IconNaoRealizada,
      bg: isDark ? '#e53935' : '#ffebee',
      filterKey: 'NAO_REALIZADA',
    },
    {
      label: 'Viagens Não Iniciadas',
      valor: indicadoresData.naoIniciadas,
      icon: IconNaoIniciada,
      bg: isDark ? '#1F6E4D' : '#fffde7',
      filterKey: 'NAO_INICIADA',
    },
    {
      label: 'Possível Desvio',
      valor: indicadoresData.canceladas,
      icon: IconCancelado,
      bg: isDark ? '#6d4c41' : '#efebe9',
      filterKey: 'CANCELADA',
    },
    {
      label: 'Ocioso Fechado',
      valor: indicadoresData.ociosoFechado || 0,
      icon: IconNaoIniciada,             // ícone já usado no projeto
      bg: isDark ? '#90a4ae' : '#eceff1',
      // sem filterKey -> NÃO filtra a tabela
      tooltipItems: divergenciasTooltip.ocioso
    },
    {
      label: 'Possível Quebra',
      valor: indicadoresData.possivelQuebra || 0,
      icon: IconAtrasado,                // ícone já usado no projeto
      bg: isDark ? '#bcaaa4' : '#fbe9e7',
      // sem filterKey -> NÃO filtra a tabela
      tooltipItems: divergenciasTooltip.quebra
    },

    {
      label: 'Viagens Extras',                 // ⬅️ novo
      valor: indicadoresData.extras,
      icon: IconExtra,
      bg: isDark ? '#7b1fa2' : '#f3e5f5',
      filterKey: 'EXTRA',
    },
    {
      label: 'Penalizações',
      valor: indicadoresData.penalizacoes,
      icon: <GavelRoundedIcon style={{ fontSize: 28 }} />,
      bg: isDark ? '#5e35b1' : '#ede7f6',
      format: fmt2, // 👈 adiciona isto
    },
    {
      label: 'Passageiros Transportados',
      valor: indicadoresData.passageiros,
      icon: IconPassageiros,
      bg: isDark ? '#8e24aa' : '#ede7f6',
    },
  ];



  const spinStyle = {
    animation: 'spin 2s linear infinite',
    '@keyframes spin': {
      '0%': { transform: 'rotate(0deg)' },
      '100%': { transform: 'rotate(360deg)' }
    }
  };


  const traduzirStatus = React.useCallback((codigo) => {
    switch (codigo) {
      case 1: return 'Executada';
      case 2: return 'Não Realizada';
      case 4: return 'Não Iniciada';
      case 5: return 'Em Andamento';
      case 6: return 'Cancelada';
      case 8: return 'Extra';
      default: return 'Indefinido';
    }
  }, []);

  const statusDerivado = React.useCallback((v) => {
    const now = dayjs();

    // SCHEDULE (programado) a partir de HH:mm[:ss]
    const hp = v?.partidaPlan ? String(v.partidaPlan) : '';
    const [h = '00', m = '00', s = '00'] = hp.split(':');
    const sched = hp
      ? now.startOf('day').hour(+h || 0).minute(+m || 0).second(+s || 0)
      : null;

    const temReal = v?.partidaReal && String(v.partidaReal).trim() !== '';
    const naoIniciou = !temReal;

    // 🚦 Prioriza status vindo da API quando já iniciou/concluiu,
    // mesmo se 'partidaReal' ainda não chegou
    const source = traduzirStatus(v?.status);
    // Se a API marcar como Não Realizada (2), mas já existe HORA REAL,
    // tratamos como Executada
    if (temReal && v?.status === 2) {
      return 'Executada';
    }

    if (!temReal && (source === 'Em Andamento' || source === 'Executada')) {
      return source;
    }
    
    // 🔹 1) Se JÁ INICIOU: detectar ADIANTADA (REAL < PROG em ≥ 5 min)
    if (temReal && sched) {
      const hr = String(v.partidaReal);
      const [rh = '00', rm = '00', rs = '00'] = hr.split(':');
      const real = now.startOf('day').hour(+rh || 0).minute(+rm || 0).second(+rs || 0);

      const adiantadoMin = sched.diff(real, 'minute'); // + se real é antes do prog
      if (adiantadoMin >= 5) return 'Adiantada';       // ⬅️ NOVO
    }

    // 🔹 2) Se AINDA NÃO INICIOU: manter sua regra de Não Iniciada / Em Atraso / Não Realizada
    if (sched && naoIniciou && now.isSame(sched, 'day')) {
      const diffMin = now.diff(sched, 'minute'); // negativo/zero = antes/igual ao horário
      if (diffMin <= 0) return 'Não Iniciada';
      if (diffMin <= 30) return 'Em Atraso';
      return 'Não Realizada';
    }

    // 🔹 3) Caso contrário, utiliza o status de origem da API
    return traduzirStatus(v?.status);
  }, [traduzirStatus]);


  useEffect(() => {
    const carregarFiltros = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/trafego/filtros`);
        // ❌ remova esta linha: setLinhasOriginais(res.data.linhas || []);
        setTrajetosOriginais(res.data.trajetos || []);
        setLinhasDisponiveis(res.data.linhas || []);
        setTrajetosDisponiveis(res.data.trajetos || []);
      } catch (err) {
        console.error('Erro ao carregar filtros:', err);
      }
    };
    carregarFiltros();
  }, []);

  // --- Helpers para filtro imediato de trajetos por linha ---
  const escapeRegExp = (s = '') => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const filtrarTrajetosLocal = (lista, linhaSel) => {
    if (!linhaSel) return lista || [];
    const re = new RegExp(`(^|_)${escapeRegExp(String(linhaSel))}(_|$)`, 'i');
    return (lista || []).filter(t => re.test(String(t)));
  };

  const handleLinhaChange = async (linhaSelecionada = '') => {
    setLinha(linhaSelecionada);
    setTrajeto(''); // limpa seleção atual do trajeto

    // Filtra localmente com base no cache de trajetos já carregados
    const imediatos = filtrarTrajetosLocal(trajetosOriginais, linhaSelecionada);
    setTrajetosDisponiveis(imediatos);
  };


  const sentidoEhIda = (v) =>
    String(v?.trajeto?.sentido || '').toLowerCase() === 'ida';

  const mapStatusFiltro = React.useMemo(() => ({
    EXECUTADA: 'Executada',
    NAO_REALIZADA: 'Não Realizada',
    NAO_INICIADA: 'Não Iniciada',
    EM_ATRASO: 'Em Atraso',
    CANCELADA: 'Cancelada',
    EXTRA: 'Extra',
    EM_ANDAMENTO: 'Em Andamento',
    ADIANTADA: 'Adiantada',                // ⬅️ NOVO
  }), []);

  // Nova regra memoizada: IDA  VOLTA com sufixo .1.. .4
  const ehPlanejadaNovoMetodo = React.useCallback((v) => {
    const sentido = String(v?.trajeto?.sentido || '').toLowerCase();
    const numeroLinha = String(v?.trajeto?.numeroLinha ?? v?.linha ?? '').trim();
    const isIda = sentido === 'ida';
    const isVoltaElegivel = sentido === 'volta' && /\.([1-4])$/.test(numeroLinha);
    return isIda || isVoltaElegivel;
  }, []);

// --- Penalizações: total do DIA CIVIL (00:00 → 23:59) ---
const fetchPenalCivilDayTotal = useCallback(async () => {
  const diaStr = dataInicio.format('YYYY-MM-DD');
  const ini = dayjs(`${diaStr} 00:00:00`);
  const fim = dayjs(`${diaStr} 23:59:59`);

  // 3 formatos comuns aceitos por APIs diferentes
  const candidates = [
    { inicio: ini.format('YYYY-MM-DD HH:mm:ss'), fim: fim.format('YYYY-MM-DD HH:mm:ss'), linhasSelecionadas: [] },
    { dataInicio: ini.format('YYYY-MM-DD HH:mm:ss'), dataFim: fim.format('YYYY-MM-DD HH:mm:ss') },
    { dataInicial: ini.format('YYYY-MM-DD HH:mm:ss'), dataFinal: fim.format('YYYY-MM-DD HH:mm:ss') },
  ];


  for (const body of candidates) {
    try {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[PENAL dia civil] tentando payload:', body);
      }
      const { data } = await axios.post(`${API_BASE}/api/penalizacoes/relatorio`, body);
      const itens = Array.isArray(data?.itens) ? data.itens : [];

      let total = 0;
      for (const r of itens) {
        const d = String(r?.data || '').trim();         // "DD/MM/AAAA"
        const h = String(r?.hora || '00:00:00').trim(); // "HH:mm[:ss]"
        const hh = h.length === 5 ? `${h}:00` : h;
        const [dd, mm, yyyy] = d.split('/');
        const dt = dayjs(`${yyyy}-${mm}-${dd} ${hh}`);
        if (!dt.isValid()) continue;
        if ((dt.isAfter(ini) || dt.isSame(ini)) && (dt.isBefore(fim) || dt.isSame(fim))) {
          const v = r?.valor ?? r?.penalizacao ?? r?.penalizacoes ?? r?.valorPenalizacao ?? r?.bravura ?? 0;
          const n = (typeof v === 'number') ? v : Number(String(v).replace(/\./g, '').replace(',', '.'));
          if (Number.isFinite(n)) total += n;
        }
      }
      return total; // sucesso
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[PENAL dia civil] falhou com payload:', body, '→', e?.response?.status, e?.message, e?.response?.data);
      }
    }
  }

  console.warn('Penalizações (dia civil) falharam após todas as tentativas.');
  return 0; // não quebra os cards
}, [dataInicio]);


  // --- Divergências (Ocioso Fechado / Possível Quebra) ---
  const fetchDivergenciasCounts = useCallback(async () => {
    const dateStr = dataInicio.format('YYYY-MM-DD');

    //   PRIORIDADE: Busca quebras reais via API de monitoramento do backend
    let quebraItems = [];
    try {
      console.log('🔍 Buscando quebras reais via backend...');
      
      // Usa o backend em vez do serviço direto
      const quebrasResp = await axios.get(`${API_BASE}/api/trafego/quebras/formatadas`);
      const quebrasFormatadas = quebrasResp.data;
      
      console.log('🔧 Dados brutos das quebras:', quebrasFormatadas);
      
      // Mapear os dados das quebras para o formato que o tooltip espera
      quebraItems = quebrasFormatadas.map(veiculo => ({
        veiculo: veiculo.veiculo, // Já vem sem o prefixo "Veículo"
        horario: new Date().toLocaleTimeString('pt-BR'),
        quebras: veiculo.quebras, // Array com duracao, horarioInicio, horarioFim
        totalQuebras: veiculo.quebras ? veiculo.quebras.length : 0
      }));
      
      console.log(`✅ Quebras detectadas: ${quebraItems.reduce((acc, v) => acc + v.totalQuebras, 0)} em ${quebraItems.length} veículos`);
    } catch (error) {
      console.warn('❌ Falha ao buscar quebras via backend:', error);
    }

    // 🔎 log de apoio (apenas quebras – ocioso vem de outro endpoint)
    console.debug('[DIV] date=', dateStr, 'quebra=', quebraItems.length);

    return {
      ocioso: null,            // não fingimos que é 0; o valor real vem de fetchOciosoFechado
      quebra: quebraItems.length,
      quebrasDetalhes: quebraItems, // 👈 dados completos para uso posterior
    };

  }, [dataInicio]);

  
  const fetchOciosoFechado = useCallback(async () => {
    try {
      // ✅ Verifica cache antes de fazer requisição
      const agora = Date.now();
      if (ociosoFechadoCache.dados && (agora - ociosoFechadoCache.timestamp) < ociosoFechadoCache.CACHE_TIME) {
        console.log(`🔄 TrafegoPage: usando cache de ocioso fechado (${Math.round((agora - ociosoFechadoCache.timestamp) / 1000)}s atrás)`);
        return ociosoFechadoCache.dados;
      }

      console.log('📡 TrafegoPage: buscando ocioso fechado (sem cache)');
      const resp = await axios.get(`${API_BASE}/api/trafego/ocioso-fechado`);
      const data = resp.data || {};

      // Aceita vários formatos de resposta do backend:
      const itens =
        Array.isArray(data)                 ? data
        : Array.isArray(data.itens)         ? data.itens
        : Array.isArray(data.veiculos)      ? data.veiculos
        : Array.isArray(data.veiculosRotaZero) ? data.veiculosRotaZero
        : [];

      // 💾 Salva em cache
      ociosoFechadoCache.dados = itens;
      ociosoFechadoCache.timestamp = Date.now();
      console.log(`💾 Cache de ocioso fechado atualizado (${itens.length} itens)`);

      return itens;
    } catch (error) {
      console.warn('❌ Falha ao buscar ocioso fechado:', error);
      return [];
    }
  }, []);

  
  const buscarViagens = useCallback(async () => {
    try {
      // Busca CACHE e LIVE em paralelo
      const [cachedResp, liveResp] = await Promise.all([
        axios.post(`${API_BASE}/api/trafego/viagens-cached`, {
          linha,
          trajeto,
          dataInicio: dataInicio ? dataInicio.format('YYYY-MM-DD') : '',
          horaInicio,
          dataFim: dataFim ? dataFim.format('YYYY-MM-DD') : '',
          horaFim
        }),
        axios.post(`${API_BASE}/api/trafego/viagens-filtradas`, {
          linha,
          trajeto,
          dataInicio: dataInicio ? dataInicio.format('YYYY-MM-DD') : '',
          horaInicio,
          dataFim: dataFim ? dataFim.format('YYYY-MM-DD') : '',
          horaFim
        }).catch(() => ({ data: [] })) // se live falhar, seguimos só com cache
      ]);

      const cached = Array.isArray(cachedResp?.data) ? cachedResp.data : [];
      const live   = Array.isArray(liveResp?.data)   ? liveResp.data   : [];

      // chave das viagens (linha|tabela|horaProg|sentido)
      const keyOf = (v) => [
        String(v?.trajeto?.numeroLinha || ''),
        String(v?.nmTabela || ''),
        String(v?.partidaPlan || ''),
        String(v?.trajeto?.sentido || '')
      ].join('|');

      const mapLive = new Map(live.map((v) => [keyOf(v), v]));

      // janela “quente” ao redor do agora (onde vale a pena sobrepor)
      const now = dayjs();
      const WINDOW_BEFORE_MIN = 20;  // até 20 min ANTES
      const WINDOW_AFTER_MIN  = 60;  // até 60 min DEPOIS
      function withinWindow(v) {
        const [h='00', m='00', s='00'] = String(v?.partidaPlan || '').split(':');
        const sched = now.startOf('day').hour(+h||0).minute(+m||0).second(+s||0);
        const diffMin = now.diff(sched, 'minute'); // + se já passou
        return diffMin >= -WINDOW_BEFORE_MIN && diffMin <= WINDOW_AFTER_MIN;
      }

      // util para traduzir status numérico → rótulo (igual ao do seu componente)
      const tStatus = (codigo) => {
        switch (codigo) {
          case 1: return 'Executada';
          case 2: return 'Não Realizada';
          case 4: return 'Não Iniciada';
          case 5: return 'Em Andamento';
          case 6: return 'Cancelada';
          case 8: return 'Extra';
          default: return 'Indefinido';
        }
      };

      // merge: mantém cache e sobrepõe campos críticos do LIVE dentro da janela
      const merged = cached.map((c) => {
        const L = mapLive.get(keyOf(c));
        if (!L || !withinWindow(c)) return c;

        const out = { ...c };

        // hora/veículo: preferir do LIVE quando o cache ainda não tiver
        if (!out.partidaReal && L.partidaReal) out.partidaReal = L.partidaReal;
        if (!out.veiculoReal && L.veiculoReal) out.veiculoReal = L.veiculoReal;

        // promoção de status quando LIVE já indica início/conclusão/cancelamento/extra
        const stC = tStatus(out.status);
        const stL = tStatus(L.status);

        const podeSubir = (
          stC === 'Não Iniciada' ||
          stC === 'Em Atraso' ||
          stC === 'Indefinido' ||
          stC === 'Não Realizada'
        );

        const alvoPrioritario = (
          stL === 'Em Andamento' ||
          stL === 'Executada' ||
          stL === 'Cancelada' ||
          stL === 'Extra'
        );

        if (podeSubir && alvoPrioritario) {
          out.status = L.status; // copia o código numérico do LIVE
        }


        return out;
      });

      // inclui no resultado final as viagens EXTRA / CANCELADA que existem só no LIVE
      const keysCached = new Set(cached.map((c) => keyOf(c)));

      const extrasLiveOnly = live.filter(
        (v) => v?.status === 8 && !keysCached.has(keyOf(v))
      );

      const canceladasLiveOnly = live.filter(
        (v) => v?.status === 6 && !keysCached.has(keyOf(v))
      );

      // fallback: se o cache vier vazio por algum motivo, use LIVE
      const dadosFinal = merged.length
        ? [...merged, ...extrasLiveOnly, ...canceladasLiveOnly]
        : live;

      // 👉 a partir daqui, use 'dadosFinal' no lugar de 'response.data'      
      // 🔹 Penalizações (card): sempre o DIA CIVIL do dataInicio (00:00 → 23:59)
      let penalTotal = 0;
      try {
        penalTotal = await fetchPenalCivilDayTotal();
      } catch (e) {
        console.warn('Penalizações (dia civil) falharam:', e?.message || e);
      }

           // Logs de apoio (remova depois)
      console.log('Exemplo de viagem:', dadosFinal?.[0]);
      console.log('Valores únicos de trajeto.sentido:', [
        ...new Set(dadosFinal.map(v => v?.trajeto?.sentido))
      ]);

      // sinaliza para o TripsGridAlerts que houve refresh (libera tocar alertas novamente)
      setRefreshSeq((s) => s + 1);

      if (Array.isArray(dadosFinal)) {

        // Divergências (Ocioso Fechado / Possível Quebra)
        let ocioso = 0;
        let quebra = 0;
        let listaOcioso = [];
        let listaQuebras = [];

        // 3.1 – POSSÍVEL QUEBRA (continua vindo de /quebras/formatadas)
        try {
          const r = await fetchDivergenciasCounts();
          quebra = r?.quebra || 0;
          listaQuebras = Array.isArray(r?.quebrasDetalhes) ? r.quebrasDetalhes : [];
        } catch (e) {
          console.warn('Possível Quebra: falha ao buscar dados:', e?.message || e);
          quebra = 0;
          listaQuebras = [];
        }

        // 3.2 – OCIOSO FECHADO (APENAS do rotaZeroService.js)
        try {
          const itensOcioso = await fetchOciosoFechado();
          const listaBruta = Array.isArray(itensOcioso) ? itensOcioso : [];

          // Formato padrão para o tooltip:
          // - veiculo: prefixo / código do veículo
          // - horario: Local + Data/Hora de transmissão (se existirem)
          listaOcioso = listaBruta.map((v) => {
            const veiculo = v.prefixo || v.veiculo || '-';

            // Status fixo para o card de Ocioso Fechado
            const status = 'OCIOSO FECHADO';

            // Quebra "DD/MM/AAAA HH:mm:ss" em Data e Hora
            let data = '-';
            let hora = '-';

            if (v.dataTransmissao) {
              const str = String(v.dataTransmissao);
              const [dataPart, horaPart] = str.split(' ');
              if (dataPart) data = dataPart;
              if (horaPart) hora = horaPart;
            }

            return {
              veiculo,
              status,
              data,
              hora,
            };
          });

          ocioso = listaOcioso.length;
        } catch (e) {
          console.warn('Ocioso Fechado: falha ao buscar dados:', e?.message || e);
          ocioso = 0;
          listaOcioso = [];
        }

        // Aplica nas tooltips e nos cards
        setDivergenciasTooltip({
          ocioso: listaOcioso,
          quebra: listaQuebras,
        });


        // Mantemos os demais indicadores calculados sobre "somenteIda" como antes
        const ida = dadosFinal.filter(sentidoEhIda);
        const somenteIda = ida.length ? ida : dadosFinal;

        // ---- NOVA METODOLOGIA PARA "VIAGENS PROGRAMADAS" ----
        // Conta todas as IDA + VOLTA cujo número da linha termina com .1.. .4
        const planejadas = dadosFinal.filter(ehPlanejadaNovoMetodo).length;

        let executadas = 0;
        let emAtraso = 0;
        let naoRealizadas = 0;
        let naoIniciadas = 0;
        let emAndamento = 0;
        let adiantadas = 0;                 // ⬅️ NOVO

        for (const v of somenteIda) {
          const st = statusDerivado(v);
          if (st === 'Executada')       executadas += 1;
          else if (st === 'Em Andamento') emAndamento += 1;
          else if (st === 'Em Atraso')  emAtraso += 1;
          else if (st === 'Não Realizada') {
            // Só considera Não Realizada se vier status 2 da API e ainda NÃO tiver HORA REAL
            if (v?.status === 2 && !v?.partidaReal) {
              naoRealizadas += 1;
            }
          }
          else if (st === 'Não Iniciada')  naoIniciadas += 1;
          else if (st === 'Adiantada')  adiantadas += 1;     // ⬅️ NOVO
        }


        // Contagens que devem considerar IDA + VOLTA
        const canceladas = dadosFinal.filter(v => statusDerivado(v) === 'Cancelada').length;
        const extras = dadosFinal.filter(v => statusDerivado(v) === 'Extra').length;

        // Passageiros — soma de ida + volta
        const passageiros = dadosFinal.reduce(
          (acc, v) => acc + (parseInt(v.qtdPassageiros || 0, 10) || 0),
          0
        );

        setViagens(dadosFinal);        
        setIndicadoresData({
          planejadas,
          executadas,
          emAndamento,
          emAtraso,
          naoRealizadas,
          naoIniciadas,
          canceladas,
          extras,
          penalizacoes: penalTotal,  // ⬅️ novo
          passageiros,
          adiantadas,                      // ⬅️ NOVO
          ociosoFechado: ocioso,
          possivelQuebra: quebra,
        });

      }
    } catch (err) {
      console.error('Erro ao buscar viagens:', err);
    }
  }, [
    linha,
    trajeto,
    dataInicio,
    horaInicio,
    dataFim,
    horaFim,
    statusDerivado,
    fetchPenalCivilDayTotal,
    ehPlanejadaNovoMetodo,
    fetchDivergenciasCounts,
    fetchOciosoFechado, // 👈 aqui
  ]);

  const viagensParaTabela = useMemo(() => {
    if (!Array.isArray(viagens)) return [];

    if (!statusFiltro) return viagens;           // sem filtro → tudo

    if (statusFiltro === 'PLANEJADAS') {
      return viagens.filter(ehPlanejadaNovoMetodo);
    }

    // Filtro específico para "Viagens Não Realizadas":
    // apenas sentido ida, status 2 da API, sem HORA REAL
    // e com statusDerivado "Não Realizada"
    if (statusFiltro === 'NAO_REALIZADA') {
      return viagens
        .filter(sentidoEhIda)
        .filter((v) =>
          v?.status === 2 &&
          !v?.partidaReal &&
          statusDerivado(v) === 'Não Realizada'
        );
    }

    const statusAlvo = mapStatusFiltro[statusFiltro];

    // Cancelada e Extra → ida + volta; demais → só ida
    const base = (statusFiltro === 'CANCELADA' || statusFiltro === 'EXTRA')
      ? viagens
      : viagens.filter(sentidoEhIda);

    return base.filter((v) => statusDerivado(v) === statusAlvo);
  }, [viagens, statusFiltro, statusDerivado, mapStatusFiltro, ehPlanejadaNovoMetodo]);

// eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => {
    buscarViagens();
  }, [buscarViagens]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev === 1) {
          buscarViagens(); // atualiza automaticamente
          return 60;       // reinicia o contador
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [buscarViagens]);

return (
  <Box sx={{ display: 'flex', minHeight: '100vh' }}>
    {/* MENU LATERAL MODERNO COM ÍCONES */}
    <Box
      sx={{
        width: 300,
        bgcolor: 'background.paper',
        p: 3,
        borderRight: '1px solid #e0e0e0',
        boxShadow: 3,
        borderRadius: '0 16px 16px 0',
        display: 'flex',
        flexDirection: 'column',
        gap: 3
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FilterAltRoundedIcon color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Filtros
        </Typography>
      </Box>

      {/* Agrupamento: Linha e Trajeto */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Linha e Trajeto
        </Typography>

        {/* Linha (com digitação e seleção) */}
        <Autocomplete
          freeSolo
          options={linhasDisponiveis}
          value={linha}
          inputValue={linha}
          onChange={(_, newValue) => {
            // seleção via menu → aplica fluxo completo (local imediato + backend)
            handleLinhaChange(newValue || '');
          }}
          onInputChange={(_, newInput) => {
            // digitação → resposta imediata local
            const val = newInput || '';
            setLinha(val);
            setTrajeto('');
            setTrajetosDisponiveis(filtrarTrajetosLocal(trajetosOriginais, val));

            // se limpar o campo, restaura tudo (e atualiza backend sem linha)
            if (!val) {
              handleLinhaChange('');
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DirectionsBusRoundedIcon sx={{ fontSize: 18 }} /> Linha
                </Box>
              }
              variant="outlined"
              fullWidth
              sx={{ borderRadius: 2, mb: 2 }}
              placeholder="Digite ou selecione a linha"
            />
          )}
        />

        {/* Trajeto (somente seleção) */}
        <FormControl fullWidth>
          <InputLabel>
            <AltRouteRoundedIcon sx={{ mr: 1, fontSize: 18 }} />Trajeto
          </InputLabel>
          <Select
            value={trajeto}
            onChange={(e) => setTrajeto(e.target.value)}
            label="Trajeto"
            sx={{ borderRadius: 2 }}
            MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
          >
            <MenuItem value="">Todos</MenuItem>
            {trajetosDisponiveis.map((t, idx) => (
              <MenuItem key={idx} value={t}>
                {t}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Agrupamento: Período */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Período
        </Typography>

        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
          <DatePicker
            label="Data Início"
            value={dataInicio}
            onChange={(newValue) => {
              if (newValue && newValue.isValid()) setDataInicio(newValue);
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                margin: 'normal',
                variant: 'outlined',
                InputLabelProps: { shrink: true },
                InputProps: {
                  startAdornment: (
                    <CalendarTodayRoundedIcon sx={{ mr: 1, color: 'primary.main' }} />
                  )
                },
                sx: { borderRadius: 2, mb: 2 }
              }
            }}
          />

          <TextField
            label="Hora Início"
            type="time"
            fullWidth
            value={horaInicio}
            onChange={(e) => setHoraInicio(e.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <AccessTimeRoundedIcon sx={{ mr: 1, color: 'primary.main' }} />
              )
            }}
            sx={{ borderRadius: 2, mb: 2 }}
          />

          <DatePicker
            label="Data Fim"
            value={dataFim}
            onChange={(newValue) => {
              if (newValue && newValue.isValid()) setDataFim(newValue);
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                margin: 'normal',
                variant: 'outlined',
                InputLabelProps: { shrink: true },
                InputProps: {
                  startAdornment: (
                    <CalendarTodayRoundedIcon sx={{ mr: 1, color: 'primary.main' }} />
                  )
                },
                sx: { borderRadius: 2, mb: 2 }
              }
            }}
          />
        </LocalizationProvider>

        <TextField
          label="Hora Fim"
          type="time"
          fullWidth
          value={horaFim}
          onChange={(e) => setHoraFim(e.target.value)}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <AccessTimeRoundedIcon sx={{ mr: 1, color: 'primary.main' }} />
            )
          }}
          sx={{ borderRadius: 2 }}
        />
      </Box>

      {/* Botão Buscar */}
      <Button
        fullWidth
        variant="contained"
        onClick={buscarViagens}
        sx={{
          mt: 1,
          borderRadius: 2,
          fontWeight: 'bold',
          py: 1.2,
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)'
        }}
      >
        Buscar
      </Button>

      {/* Botão Limpar Filtros */}
      <Button
        fullWidth
        variant="outlined"
        onClick={() => {
          setLinha('');
          setTrajeto('');
          setTrajetosDisponiveis(trajetosOriginais);
          setDataInicio(dayjs());
          setHoraInicio(horaInicioPadrao);
          setDataFim(dayjs().add(1, 'day'));
          setHoraFim(horaFimPadrao);
          // setStatusFiltro(null); // se quiser limpar o filtro dos cards
        }}
        sx={{
          mt: 1,
          borderRadius: 2,
          fontWeight: 'bold',
          py: 1.2,
          color: '#FFFFFF',
          backgroundColor: '#05FFA1',
          '&:hover': { backgroundColor: '#04E891' }
        }}
      >
        Limpar Filtros
      </Button>
    </Box>

    {/* CONTEÚDO PRINCIPAL */}
    <Box sx={{ flex: 1, p: 4 }}>
      {/* Abas */}
      <Paper
        elevation={0}
        sx={{
          p: 0.5,
          borderRadius: 999,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'inline-flex',
          mb: 2,
        }}
      >
        <PillTabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          <PillTab
            icon={<DirectionsBusRoundedIcon fontSize="small" />}
            iconPosition="start"
            label="Operação"
          />
          <PillTab
            icon={<GavelRoundedIcon fontSize="small" />}
            iconPosition="start"
            label="Penalização"
          />
        </PillTabs>
      </Paper>


      {/* ABA 0 — conteúdo atual do Tráfego */}
      <TabPanel value={tab} index={0}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            mb: 3,
          }}
        >
          {/* Indicadores */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {indicadores.map((item, idx) => {
              const card = (
                <Paper
                  key={idx}
                  onClick={() => {
                    if (!item.filterKey) return;
                    setStatusFiltro((prev) => (prev === item.filterKey ? null : item.filterKey));
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    minWidth: 240,
                    backgroundColor: item.bg,
                    borderLeft: '6px solid #1976d2',
                    borderRadius: 2,
                    boxShadow: 3,
                    cursor: item.filterKey ? 'pointer' : 'default',
                    outline: item.filterKey && statusFiltro === item.filterKey ? '2px solid #1976d2' : 'none',
                    opacity: item.filterKey && statusFiltro && statusFiltro !== item.filterKey ? 0.65 : 1,
                    transition: 'outline 0.15s ease, opacity 0.15s ease',
                  }}
                >
                  {typeof item.icon === 'string' ? (
                    <Box component="img" src={item.icon} alt={item.label} sx={{ width: 40, height: 40 }} />
                  ) : (
                    <Box sx={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {item.icon}
                    </Box>
                  )}
                  <Box>
                    <Typography variant="h6">
                      {item.format
                        ? item.format(item.valor)
                        : new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(item.valor)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={(theme) => ({
                        color: theme.palette.mode === "dark" ? "#FFFFFF" : theme.palette.text.secondary,
                      })}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                </Paper>
              );

              // Se houver itens de tooltip (apenas nos dois cards novos), exibe Veículo — Horário
              if (Array.isArray(item.tooltipItems) && item.tooltipItems.length) {
                return (
                  <Tooltip
                    key={idx}
                    title={
                      <CardDivergenciasTooltip
                        items={item.tooltipItems}
                        variant={item.label === 'Ocioso Fechado' ? 'ocioso' : 'quebra'}
                      />
                    }
                    arrow
                    placement="bottom-start"
                    disableInteractive={false}
                    enterDelay={120}
                    leaveDelay={80}
                    componentsProps={{
                      tooltip: { sx: { p: 0, bgcolor: 'transparent', boxShadow: 'none' } },
                      arrow:   { sx: { color: (theme) => theme.palette.background.paper } },
                      popper:  { modifiers: [{ name: 'offset', options: { offset: [0, 10] } }] },
                    }}
                  >
                    <span>{card}</span>
                  </Tooltip>
                );

              }

              return card;
            })}
          </Box>

          {/* Controles à direita */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Atualização em {countdown}s
            </Typography>

            <AutorenewRoundedIcon sx={spinStyle} />
            <IconButton onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')} color="inherit">
              {mode === 'dark' ? <LightModeRoundedIcon /> : <DarkModeRoundedIcon />}
            </IconButton>
            <Button variant="outlined" onClick={() => navigate('/menu')}>Voltar</Button>
          </Box>
        </Box>

        {/* Tabela */}
        <Box sx={{ mt: 1 }}>
          <TripsGridAlerts
            viagens={viagensParaTabela}
            statusDerivado={statusDerivado}
            getStatusComIcone={getStatusComIcone}
            pageSize={10}          // 10 linhas por página
            refreshSeq={refreshSeq}
              loading={false}
          />
        </Box>

        {/* Mapa em tempo real com quebras integradas ao Tráfego */}
        <Box sx={{ mt: 3 }}>
          <MapaTrafego />
        </Box>

      </TabPanel>

      {/* ABA 1 — Penalização */}
      <TabPanel value={tab} index={1}>
        <PenalizacaoPanel periodo={periodoSelecionado} />
      </TabPanel>

    </Box>
    </Box>
);
}